import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useColorTheme } from "context/ColorThemeContext";
import CustomTextField from "components/TextField";
import { fetchCategoryCreate, fetchCategoryUpdate } from "api/categoryService";

function AddCategory({ open, onClose, editMode, initialData, refreshDataAfterChange }) {
  const colors = useColorTheme();
  const [form, setForm] = useState({ name: "", displayOrder: 1 });
  const [responseMessage, setResponseMessage] = useState("");

  useEffect(() => {
    if (editMode && initialData) {
      setForm({
        name: initialData.categoryName,
        displayOrder: initialData.displayOrder || 1,
      });
    } else {
      setForm({ name: "", displayOrder: 1 });
    }
    setResponseMessage(""); // reset lỗi khi mở dialog mới
  }, [editMode, initialData]);

  const validateForm = () => {
    if (!form.name?.trim()) {
      setResponseMessage("Nhập thông tin bắt buộc");
      return false;
    }
    if (form.displayOrder < 1) {
      setResponseMessage("Thứ tự hiển thị phải lớn hơn hoặc bằng 1");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    const payload = {
      categoryName: (form.name || "").trim(),
      displayOrder: form.displayOrder,
      categoryCode: "", // Nếu backend tự sinh mã
    };

    const result = editMode
      ? await fetchCategoryUpdate(initialData.categoryId, payload)
      : await fetchCategoryCreate(payload);

    if (result.success) {
      Swal.fire({
        icon: "success",
        title: editMode ? "Cập nhật thành công" : "Thêm mới thành công",
        showConfirmButton: false,
        timer: 1500,
      });

      refreshDataAfterChange();
      setForm({ name: "", displayOrder: 1 });
      setResponseMessage("");
      onClose();
    } else {
      setResponseMessage(result.data?.message || "Có lỗi xảy ra");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px", // Bo góc giống hình
          p: 3, // Padding tổng thể
          background: colors.popupBackground || "#f2f2f2",
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: "center", // Căn giữa tiêu đề
          fontWeight: "bold",
          fontSize: "24px",
          pb: 0,
          mt: 2,
        }}
      >
        {editMode ? "Chỉnh sửa danh mục" : "Thêm mới danh mục"}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "#000",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <CustomTextField
          label="Tên danh mục"
          placeholder="Nhập tên danh mục"
          value={form.name}
          onChange={(e) => {
            setForm((prev) => ({ ...prev, name: e.target.value }));
            setResponseMessage("");
          }}
          fullWidth
          required
          error={
            responseMessage === "Tên danh mục đã tồn tại" ||
            ((form.name || "").trim() === "" && responseMessage === "Nhập thông tin bắt buộc")
          }
          helperText={
            responseMessage === "Tên danh mục đã tồn tại" ||
            ((form.name || "").trim() === "" && responseMessage === "Nhập thông tin bắt buộc")
              ? responseMessage
              : ""
          }
        />

        <CustomTextField
          label="Thứ tự hiển thị"
          type="number"
          value={form.displayOrder}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              displayOrder: Number(e.target.value),
            }))
          }
          fullWidth
          required
          error={responseMessage === "Thứ tự hiển thị phải lớn hơn hoặc bằng 1"}
          helperText={
            responseMessage === "Thứ tự hiển thị phải lớn hơn hoặc bằng 1" ? responseMessage : ""
          }
        />
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center" }}>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{
            backgroundColor: colors.buttonBackground || "#2196F3",
            color: colors.popupText || "white",
            borderRadius: "8px",
            paddingX: 4,
            paddingY: 1,
            textTransform: "none",
            fontWeight: 500,
            fontSize: "15px",
            "&:hover": {
              backgroundColor: "#1976D2",
            },
          }}
        >
          {editMode ? "Cập nhật" : "Thêm mới"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AddCategory.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  editMode: PropTypes.bool,
  initialData: PropTypes.shape({
    categoryId: PropTypes.number,
    categoryName: PropTypes.string,
    displayOrder: PropTypes.number,
  }),
  refreshDataAfterChange: PropTypes.func.isRequired,
};

AddCategory.defaultProps = {
  editMode: false,
  initialData: null,
};

export default AddCategory;
